import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import api from "@/lib/api";
import {
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  Users,
  Download,
  ExternalLink,
  Share2,
  Copy,
  Link as LinkIcon,
  RefreshCw,
  Check
} from "lucide-react";
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameMonth,
  isToday,
  getDay,
  addMonths,
  subMonths,
  isWithinInterval,
  parseISO,
  addDays
} from "date-fns";
import { es } from "date-fns/locale";



const DAYS_OF_WEEK = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];
const MONTHS = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
];

// Color palettes for predefined departments
const DEPARTMENT_COLORS = {
  "tecnologia": { bg: "bg-sky-50/70 border-sky-200", border: "border-sky-500", text: "text-sky-800", bullet: "bg-sky-500", borderL: "border-l-sky-500", ring: "ring-sky-500", activeBg: "bg-sky-100 text-sky-800 border-sky-300" },
  "operaciones": { bg: "bg-orange-50/70 border-orange-200", border: "border-orange-500", text: "text-orange-800", bullet: "bg-orange-500", borderL: "border-l-orange-500", ring: "ring-orange-500", activeBg: "bg-orange-100 text-orange-800 border-orange-300" },
  "recursos humanos": { bg: "bg-pink-50/70 border-pink-200", border: "border-pink-500", text: "text-pink-800", bullet: "bg-pink-500", borderL: "border-l-pink-500", ring: "ring-pink-500", activeBg: "bg-pink-100 text-pink-800 border-pink-300" },
  "finanzas": { bg: "bg-emerald-50/70 border-emerald-200", border: "border-emerald-500", text: "text-emerald-800", bullet: "bg-emerald-500", borderL: "border-l-emerald-500", ring: "ring-emerald-500", activeBg: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  "ventas": { bg: "bg-violet-50/70 border-violet-200", border: "border-violet-500", text: "text-violet-800", bullet: "bg-violet-500", borderL: "border-l-violet-500", ring: "ring-violet-500", activeBg: "bg-violet-100 text-violet-800 border-violet-300" },
  "administracion": { bg: "bg-indigo-50/70 border-indigo-200", border: "border-indigo-500", text: "text-indigo-800", bullet: "bg-indigo-500", borderL: "border-l-indigo-500", ring: "ring-indigo-500", activeBg: "bg-indigo-100 text-indigo-800 border-indigo-300" },
  "mercadeo": { bg: "bg-amber-50/70 border-amber-200", border: "border-amber-500", text: "text-amber-800", bullet: "bg-amber-500", borderL: "border-l-amber-500", ring: "ring-amber-500", activeBg: "bg-amber-100 text-amber-800 border-amber-300" },
  "default": { bg: "bg-slate-50 border-slate-200", border: "border-slate-400", text: "text-slate-700", bullet: "bg-slate-400", borderL: "border-l-slate-400", ring: "ring-slate-400", activeBg: "bg-slate-100 text-slate-700 border-slate-300" }
};

// Premium colors for other departments
const FALLBACK_PALETTES = [
  { bg: "bg-teal-50/70 border-teal-200", border: "border-teal-500", text: "text-teal-800", bullet: "bg-teal-500", borderL: "border-l-teal-500", ring: "ring-teal-500", activeBg: "bg-teal-100 text-teal-800 border-teal-300" },
  { bg: "bg-cyan-50/70 border-cyan-200", border: "border-cyan-500", text: "text-cyan-800", bullet: "bg-cyan-500", borderL: "border-l-cyan-500", ring: "ring-cyan-500", activeBg: "bg-cyan-100 text-cyan-800 border-cyan-300" },
  { bg: "bg-fuchsia-50/70 border-fuchsia-200", border: "border-fuchsia-500", text: "text-fuchsia-800", bullet: "bg-fuchsia-500", borderL: "border-l-fuchsia-500", ring: "ring-fuchsia-500", activeBg: "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300" },
  { bg: "bg-rose-50/70 border-rose-200", border: "border-rose-500", text: "text-rose-800", bullet: "bg-rose-500", borderL: "border-l-rose-500", ring: "ring-rose-500", activeBg: "bg-rose-100 text-rose-800 border-rose-300" },
  { bg: "bg-purple-50/70 border-purple-200", border: "border-purple-500", text: "text-purple-800", bullet: "bg-purple-500", borderL: "border-l-purple-500", ring: "ring-purple-500", activeBg: "bg-purple-100 text-purple-800 border-purple-300" },
  { bg: "bg-lime-50/70 border-lime-200", border: "border-lime-500", text: "text-lime-800", bullet: "bg-lime-500", borderL: "border-l-lime-500", ring: "ring-lime-500", activeBg: "bg-lime-100 text-lime-800 border-lime-300" },
  { bg: "bg-red-50/70 border-red-200", border: "border-red-500", text: "text-red-800", bullet: "bg-red-500", borderL: "border-l-red-500", ring: "ring-red-500", activeBg: "bg-red-100 text-red-800 border-red-300" }
];

const normalizeDeptName = (str) => {
  if (!str) return "";
  return str
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
};

const getDeptColors = (dept) => {
  if (!dept) return DEPARTMENT_COLORS["default"];
  const clean = normalizeDeptName(dept);
  
  // Try to match predefined departments
  if (DEPARTMENT_COLORS[clean]) {
    return DEPARTMENT_COLORS[clean];
  }
  
  // Stably hash other departments to distinct fallback palettes
  let hash = 0;
  for (let i = 0; i < clean.length; i++) {
    hash = clean.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % FALLBACK_PALETTES.length;
  return FALLBACK_PALETTES[index];
};

const CalendarioPage = () => {
  const { user } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSyncLink, setShowSyncLink] = useState(false);
  const [selectedDept, setSelectedDept] = useState("Todos");

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const response = await api.getCalendarEvents(
        currentDate.getMonth() + 1,
        currentDate.getFullYear()
      );
      setEvents(response.data);
    } catch (error) {
      console.error("Error fetching calendar events:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, [currentDate]);

  const goToPreviousMonth = () => setCurrentDate(subMonths(currentDate, 1));
  const goToNextMonth = () => setCurrentDate(addMonths(currentDate, 1));
  const goToToday = () => setCurrentDate(new Date());

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const calendarDays = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const startDayOfWeek = getDay(monthStart);
  const paddingDays = Array(startDayOfWeek).fill(null);

  const isAlreadyTaken = (endDateStr) => {
    const todayStr = format(new Date(), "yyyy-MM-dd");
    return endDateStr < todayStr;
  };

  const filteredEvents = selectedDept === "Todos"
    ? events
    : events.filter(e => (e.departamento || "Sin Depto.") === selectedDept);

  const getEventsForDay = (day) => {
    if (!day) return [];
    const dayStr = format(day, "yyyy-MM-dd");
    return filteredEvents.filter((event) => {
      const startDate = parseISO(event.fecha_inicio);
      const endDate = parseISO(event.fecha_fin);
      const currentDay = parseISO(dayStr);
      return isWithinInterval(currentDay, { start: startDate, end: endDate });
    });
  };

  const baseUrl = typeof window !== "undefined" ? window.location.origin : (process.env.REACT_APP_BACKEND_URL || "https://timeoff.cpslogistics.com");
  const syncUrl = `${baseUrl.replace(/\/+$/, "")}/api/calendario/feed/${user?.id}`;

  const copySyncLink = () => {
    navigator.clipboard.writeText(syncUrl);
    toast.success("Link de sincronización copiado");
  };

  const downloadICS = () => {
    if (events.length === 0) return;
    let icsContent = "BEGIN:VCALENDAR\nVERSION:2.0\nPRODID:-//VacaGest//ES\n";
    events.forEach(event => {
      const start = format(parseISO(event.fecha_inicio), "yyyyMMdd'T090000Z'");
      const end = format(addDays(parseISO(event.fecha_fin), 1), "yyyyMMdd'T180000Z'");
      icsContent += `BEGIN:VEVENT\nSUMMARY:Vacaciones ${event.empleado_nombre}\nDTSTART:${start}\nDTEND:${end}\nEND:VEVENT\n`;
    });
    icsContent += "END:VCALENDAR";
    const blob = new Blob([icsContent], { type: "text/calendar;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `vacaciones_${format(currentDate, "MMM_yyyy")}.ics`;
    link.click();
  };

  const getUniqueDepartments = () => {
    const counts = {};
    events.forEach((event) => {
      const dept = event.departamento || "Sin Depto.";
      counts[dept] = (counts[dept] || 0) + 1;
    });
    return Object.entries(counts).map(([name, count]) => ({
      name,
      count,
    })).sort((a, b) => a.name.localeCompare(b.name));
  };

  const activeDepts = getUniqueDepartments();
  const totalCount = events.length;

  return (
    <div className="space-y-6 animate-fadeIn pb-10">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800 tracking-tight">Calendario de Vacaciones</h1>
          <p className="text-slate-500 mt-1">Visualiza las vacaciones aprobadas del equipo</p>
        </div>

        <div className="flex items-center gap-2">

          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowSyncLink(!showSyncLink)}
            className={`border-slate-200 ${showSyncLink ? 'bg-sky-50 border-sky-300 text-sky-700' : 'text-slate-600'}`}
          >
            <Share2 className="w-4 h-4 mr-2" />
            Vincular (iCal)
          </Button>
        </div>
      </div>

      {showSyncLink && (
        <Card className="border-sky-100 bg-sky-50/30 animate-slideDown">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="space-y-1">
                <h4 className="text-sm font-semibold text-sky-900 flex items-center gap-2">
                  <LinkIcon className="w-4 h-4" />
                  Link de Sincronización Automática
                </h4>
                <div className="text-xs text-sky-700 space-y-1 mt-1">
                  <p>1. Copia este link.</p>
                  <p>2. En Google Calendar, ve a <strong>"Añadir otros calendarios"</strong> (símbolo +).</p>
                  <p>3. Selecciona <strong>"Desde URL"</strong>, pega el link y haz clic en "Añadir calendario".</p>
                  <p className="mt-2 text-[11px] opacity-80">Google sincronizará automáticamente todos los eventos de tu departamento.</p>
                </div>
              </div>
              <div className="flex w-full md:w-auto gap-2">
                <Input value={syncUrl} readOnly className="h-9 text-xs bg-white border-sky-200" />
                <Button size="sm" onClick={copySyncLink} className="bg-sky-600 hover:bg-sky-700">
                  <Copy className="w-4 h-4 mr-2" /> Copiar
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Calendar Header */}
      <Card className="border-slate-100 shadow-sm">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={goToPreviousMonth}><ChevronLeft className="w-4 h-4" /></Button>
              <Button variant="outline" size="icon" onClick={goToNextMonth}><ChevronRight className="w-4 h-4" /></Button>
              <Button variant="ghost" onClick={goToToday}>Hoy</Button>
            </div>
            <h2 className="text-xl font-bold text-slate-800">{MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}</h2>
            <div className="flex items-center gap-2">
               <Badge className="bg-sky-100 text-sky-700 border-sky-200">Aprobadas</Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Grid: Sidebar + Calendar */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 items-start">
        {/* Sidebar: Departamentos */}
        <div className="lg:col-span-1 space-y-4">
          <Card className="border-slate-100 shadow-sm overflow-hidden">
            <CardHeader className="bg-slate-50/50 border-b border-slate-100 pb-3">
              <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-500">Departamentos</CardTitle>
              <CardDescription className="text-xs">Filtra el calendario por área</CardDescription>
            </CardHeader>
            <CardContent className="space-y-1.5 p-3">
              <Button
                variant={selectedDept === "Todos" ? "default" : "ghost"}
                className={`w-full justify-between h-9 text-xs font-semibold rounded-lg ${
                  selectedDept === "Todos" 
                    ? "bg-slate-900 text-white hover:bg-slate-800" 
                    : "text-slate-600 hover:bg-slate-50"
                }`}
                onClick={() => setSelectedDept("Todos")}
              >
                <span className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-slate-400"></span>
                  Todos
                </span>
                <Badge variant="outline" className={`text-[10px] px-1.5 py-0 border-slate-200 bg-slate-50 ${selectedDept === "Todos" ? "text-slate-900 border-white bg-slate-800" : ""}`}>
                  {totalCount}
                </Badge>
              </Button>
              
              {activeDepts.map(({ name, count }) => {
                const colors = getDeptColors(name);
                const isSelected = selectedDept === name;
                return (
                  <Button
                    key={name}
                    variant={isSelected ? "default" : "ghost"}
                    className={`w-full justify-between h-9 text-xs font-semibold rounded-lg transition-all ${
                      isSelected 
                        ? `${colors.activeBg} hover:opacity-90` 
                        : "text-slate-600 hover:bg-slate-50"
                    }`}
                    onClick={() => setSelectedDept(name)}
                  >
                    <span className="flex items-center gap-2 truncate">
                      <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${colors.bullet}`}></span>
                      <span className="truncate">{name}</span>
                    </span>
                    <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${isSelected ? "bg-white/80 border-slate-300 text-slate-800" : "bg-slate-50 border-slate-100"}`}>
                      {count}
                    </Badge>
                  </Button>
                );
              })}

              {activeDepts.length === 0 && (
                <p className="text-xs text-slate-400 py-3 text-center">No hay áreas activas.</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Column Principal: Calendario */}
        <div className="lg:col-span-3 space-y-6">
          <Card className="border-slate-100 overflow-hidden shadow-sm">
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center h-96">
                  <div className="w-10 h-10 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : (
                <div className="grid grid-cols-7">
                  {DAYS_OF_WEEK.map((day) => (
                    <div key={day} className="p-3 text-center text-xs font-bold text-slate-500 bg-slate-50/50 border-b border-slate-100 uppercase tracking-wider">{day}</div>
                  ))}
                  {paddingDays.map((_, index) => (
                    <div key={`padding-${index}`} className="min-h-[100px] p-1.5 bg-slate-50/20 border-b border-r border-slate-100/50"></div>
                  ))}
                  {calendarDays.map((day) => {
                    const dayEvents = getEventsForDay(day);
                    const isCurrentDay = isToday(day);
                    const isWeekend = getDay(day) === 0 || getDay(day) === 6;
                    return (
                      <div key={day.toISOString()} className={`min-h-[100px] p-1.5 border-b border-r border-slate-100 transition-colors ${isWeekend ? "bg-slate-50/40" : "bg-white hover:bg-slate-50/50"}`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className={`w-6 h-6 flex items-center justify-center text-xs font-semibold rounded-full ${isCurrentDay ? "bg-orange-500 text-white font-bold" : "text-slate-500"}`}>{format(day, "d")}</span>
                        </div>
                        <div className="space-y-1">
                          {dayEvents.slice(0, 3).map((event, index) => {
                            const colors = getDeptColors(event.departamento);
                            const taken = isAlreadyTaken(event.fecha_fin);
                            return (
                              <div 
                                key={`${event.id}-${index}`} 
                                className={`text-[9px] p-0.5 px-1.5 rounded truncate flex items-center gap-1 border border-l-2 ${colors.bg} ${colors.text} ${colors.border}`} 
                                title={`${event.empleado_nombre} (${event.departamento || 'Sin Depto.'}): ${event.motivo}${taken ? ' (Ya gozadas)' : ''}`}
                              >
                                {taken && <Check className="w-2.5 h-2.5 shrink-0 text-emerald-600 font-bold" />}
                                <span className="truncate">{event.empleado_nombre?.split(" ")[0]}</span>
                              </div>
                            );
                          })}
                          {dayEvents.length > 3 && (
                            <div className="text-[8px] text-slate-400 font-bold text-center mt-1">
                              +{dayEvents.length - 3} más
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* List view of selected department events */}
          {filteredEvents.length > 0 && (
            <Card className="border-slate-100 shadow-sm overflow-hidden">
              <CardHeader className="bg-slate-50/50 border-b border-slate-100">
                <CardTitle className="text-lg">Vacaciones del Mes ({filteredEvents.length})</CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {filteredEvents.map((event) => {
                    const colors = getDeptColors(event.departamento);
                    const taken = isAlreadyTaken(event.fecha_fin);
                    return (
                      <div key={event.id} className={`flex items-center justify-between p-3.5 rounded-xl border transition-colors ${colors.bg} ${colors.border} group`}>
                        <div className="flex items-center gap-3">
                          <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${taken ? 'bg-emerald-100 text-emerald-700' : 'bg-white shadow-sm border border-slate-100 text-slate-500'}`}>
                            {taken ? <Check className="w-4 h-4" /> : <CalendarIcon className="w-4 h-4" />}
                          </div>
                          <div className="space-y-0.5">
                            <div className="flex items-center gap-2">
                              <p className="font-bold text-sm text-slate-800">{event.empleado_nombre}</p>
                              <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-white/60 text-slate-600 border border-slate-100/50">{event.departamento || "Sin Depto."}</span>
                            </div>
                            <p className="text-[11px] text-slate-500">
                              {format(parseISO(event.fecha_inicio), "d 'de' MMM", { locale: es })} al {format(parseISO(event.fecha_fin), "d 'de' MMM", { locale: es })}
                              {taken && <span className="ml-2 font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-100">Gozadas</span>}
                            </p>
                          </div>
                        </div>
                        <Badge variant="outline" className="bg-white/80 font-bold border-slate-200 text-slate-700 text-xs">{event.dias_solicitados} días</Badge>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default CalendarioPage;
